import { useEffect, useMemo, useState } from "react"
import { MdAdsClick, MdWarningAmber } from "react-icons/md"
import Modal from "../../components/Modal"
import Button from "../../components/Form/Button"
import Checkbox from "../../components/Form/Checkbox"
import Input from "../../components/Form/Input"
import Loading from "../layouts/Loading"
import api from "../../services/api"
import styles from "./metaIntegration.module.css"

const PAGE_SIZE = 100
const normalizeAdAccountId = value => String(value || "").replace(/^act_/, "").replace(/\D/g, "")

const ModalMetaAdAccountSelection = ({ connection, closeModal, onSaved }) => {
    const [accounts, setAccounts] = useState([])
    const [dashboard, setDashboard] = useState(new Set())
    const [search, setSearch] = useState("")
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        api.post("/integrations/meta/adAccounts/discover", { meta_user_account_id: connection._id })
            .then(response => {
                const data = response?.data?.data || {}
                setAccounts((data.accounts || []).map(account => ({ ...account, id: normalizeAdAccountId(account.id) })).filter(account => account.id))
                setDashboard(new Set((data.dashboard_ids || []).map(normalizeAdAccountId).filter(Boolean)))
            })
            .catch(err => setError(err?.response?.data?.message || "Não foi possível consultar as contas na Meta."))
            .finally(() => setLoading(false))
    }, [connection._id])

    const filtered = useMemo(() => {
        const term = search.trim().toLocaleLowerCase("pt-BR")
        return accounts.filter(account => !term || `${account.name || ""} ${account.id || ""} ${account.business?.name || ""}`.toLocaleLowerCase("pt-BR").includes(term))
    }, [accounts, search])

    const toggle = (setter, id) => setter(previous => {
        const next = new Set(previous)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
    })

    const authorizeFiltered = () => {
        const ids = filtered.map(account => normalizeAdAccountId(account.id)).filter(Boolean)
        setDashboard(previous => new Set([...previous, ...ids]))
    }

    const clearFiltered = () => {
        const ids = new Set(filtered.map(account => normalizeAdAccountId(account.id)))
        setDashboard(previous => new Set([...previous].filter(id => !ids.has(id))))
    }

    const save = async () => {
        setSaving(true)
        setError("")
        try {
            await api.patch("/integrations/meta/adAccounts/selection", {
                meta_user_account_id: connection._id,
                dashboard_ids: [...dashboard],
            })
            await onSaved?.()
            closeModal()
        } catch (err) {
            setError(err?.response?.data?.message || "Não foi possível salvar as contas autorizadas.")
        } finally { setSaving(false) }
    }

    return <Modal icon={<MdAdsClick />} title="Contas Meta Ads autorizadas" description={`Defina onde cada conta de ${connection.name || "esta conexão"} poderá ser usada na Oktto.`} closeModal={saving ? () => {} : closeModal} closeOnEsc={!saving} bodyClassName={styles.assetSelectionBody}>
        {loading ? <div className={styles.selectionLoading}><Loading /><p>Consultando as contas diretamente na Meta…</p></div> : <>
            <div className={styles.selectionNotice}><MdWarningAmber /><span>Somente as contas marcadas serão visíveis nesta organização. As contas permitem consultar métricas no Dashboard. Autorize os pixels separadamente para enviar conversões.</span></div>
            <div className={styles.selectionToolbar}>
                <Input type="search" value={search} onChange={event => { setSearch(event.target.value); setVisibleCount(PAGE_SIZE) }} placeholder="Pesquisar por nome, ID ou portfólio" aria-label="Pesquisar contas Meta Ads" />
                <span><strong>{dashboard.size}</strong> de {accounts.length} autorizadas</span>
            </div>
            <div className={styles.selectionQuickActions}><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={authorizeFiltered}>Autorizar resultados</Button><Button type="button" variant="secondary" size="sm" fullWidth={false} onClick={clearFiltered}>Limpar resultados</Button></div>
            {error && <div className={styles.selectionError}>{error}</div>}
            <div className={styles.selectionList}>
                {filtered.slice(0, visibleCount).map(account => {
                    const id = normalizeAdAccountId(account.id)
                    return <div className={styles.selectionRow} key={id}>
                        <span className={styles.selectionIdentity}><MdAdsClick /><span><strong>{account.name || "Conta Meta Ads"}</strong><small>{id}{account.currency ? ` · ${account.currency}` : ""}{account.business?.name ? ` · ${account.business.name}` : ""}</small></span></span>
                        <div className={styles.selectionPermissions}>
                            <button type="button" aria-pressed={dashboard.has(id)} onClick={() => toggle(setDashboard, id)}><Checkbox checked={dashboard.has(id)} /><span><strong>Dashboard</strong><small>Métricas e relatórios</small></span></button>
                        </div>
                    </div>
                })}
                {!filtered.length && <p className={styles.emptySelection}>Nenhuma conta encontrada.</p>}
                {visibleCount < filtered.length && <Button type="button" variant="secondary" fullWidth={false} onClick={() => setVisibleCount(value => value + PAGE_SIZE)}>Carregar mais</Button>}
            </div>
            <div className={styles.selectionFooter}><Button type="button" variant="secondary" fullWidth={false} onClick={closeModal}>Cancelar</Button><Button type="button" fullWidth={false} onClick={save}>Salvar {dashboard.size} conta(s)</Button></div>
        </>}
        {saving && <Loading fullPage={true} />}
    </Modal>
}

export default ModalMetaAdAccountSelection
